import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, keys } from "@shared/api";
import type { Message, Named, Uuid } from "@shared/api";
import { ToggleGroup, ToggleGroupItem } from "@shared/ui/toggle-group";
import { plainText } from "@shared/lib/markup";
import { dayTime, plural } from "@shared/lib/format";
import { fromSelf, livePerson, Tile } from "@entities/user";
import { useMe } from "@entities/session";
import { IconFilters } from "@shared/ui/icons";
import { filterCount, filtersOf, toRequest, useSearch } from "../model/search";

/**
 * Находки поиска — колонкой справа, как в Discord: набирают и выбирают
 * фильтры в поле шапки (`SearchField`), здесь — что нашлось. По всему серверу
 * или в этом канале, свежими сверху, совпавшие слова подсвечены, «Перейти»
 * ведёт к находке — и в другой канал тоже.
 *
 * Ищет сервер: слова по началу и с русской морфологией, так что «кош» находит
 * и «кошку», и «кошек». Подсветка здесь проще — по началу слова, без
 * морфологии: она подсказывает, где смотреть, а не решает, что нашлось.
 */
export function SearchResults({
  channelId,
  serverId,
  channelName,
  people,
  onOpen,
}: {
  channelId: Uuid;
  /** `null` — личная переписка: искать можно только в ней. */
  serverId: Uuid | null;
  channelName: (id: Uuid) => string | null;
  people: Map<Uuid, Named>;
  /** Перейти к находке — она бывает и в другом канале. */
  onOpen: (message: Message) => void;
}) {
  const me = fromSelf(useMe());
  const search = useSearch();
  const { query, scope, setScope, sort, setSort, setEditing } = search;
  const filters = filtersOf(search);
  const count = filterCount(filters);
  // «в:» ищет по серверу: каналы выбраны — значит, весь сервер, сколько бы ни стоял переключатель.
  // В ветке ищут по её каналу: ветка живёт под ним.
  const where = (scope === "server" || filters.channels.length > 0) && serverId && !filters.thread ? serverId : channelId;
  const words = useMemo(() => (query ?? "").toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [], [query]);
  const filtered = query !== null && (words.length > 0 || count > 0);
  const request = { ...toRequest(filters), sort };
  const { data: found, isFetching } = useQuery({
    queryKey: keys.search(where, query ?? "", request),
    queryFn: () =>
      where === serverId
        ? api.messages.searchServer(where, query ?? "", request)
        : api.messages.search(where, query ?? "", request),
    enabled: filtered,
    staleTime: 30_000,
  });

  return (
    <>
      <div className="found-bar">
        <span className="found-total">
          {!filtered
            ? "Поиск"
            : isFetching && !found
              ? "Ищу…"
              : `${found?.length ?? 0} ${plural(found?.length ?? 0, "находка", "находки", "находок")}`}
        </span>
        <button type="button" className="found-filters-btn" onClick={() => setEditing(true)}>
          <IconFilters size={14} /> Фильтры{count > 0 ? ` (${count})` : ""}
        </button>
        {serverId && (
          <ToggleGroup
            value={scope}
            onValueChange={(next) => next && setScope(next as typeof scope)}
            aria-label="Где искать"
            className="found-scope"
          >
            <ToggleGroupItem value="server">Весь сервер</ToggleGroupItem>
            <ToggleGroupItem value="channel">Этот канал</ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>

      {filtered && (
        <ToggleGroup
          value={sort}
          onValueChange={(next) => next && setSort(next as typeof sort)}
          aria-label="Порядок находок"
          className="found-sort"
        >
          <ToggleGroupItem value="newest">Новые</ToggleGroupItem>
          <ToggleGroupItem value="oldest">Старые</ToggleGroupItem>
          {/* Без слов совпадать не с чем — сервер тогда отдаёт новые сверху. */}
          {words.length > 0 && <ToggleGroupItem value="relevant">По совпадению</ToggleGroupItem>}
        </ToggleGroup>
      )}

      <div className="found-list">
        {!filtered ? (
          <div className="side-col-note">
            Наберите слова в поле поиска в шапке и нажмите Enter. Слова ищутся по началу и в любой
            форме: «кош» найдёт и «кошку», и «кошек». Можно и без слов — только «от кого», «что есть»
            или «когда».
          </div>
        ) : found && !isFetching && found.length === 0 ? (
          <div className="side-col-note">Ничего не нашлось.</div>
        ) : (
          found && (
            <>
              {found.length >= 25 && (
                <div className="found-count">
                  {sort === "oldest" ? "Показаны первые" : sort === "relevant" && words.length > 0 ? "Показаны самые подходящие" : "Показаны последние"}
                  {" — уточните запрос"}
                </div>
              )}
              {found.map((message) => {
                const who = livePerson(message.author, people, me);
                return (
                  <article key={message.id} className="found-card">
                    <header className="found-head">
                      <Tile person={who} size={22} mark={null} />
                      <span className="ellipsis">{who.name}</span>
                      {where === serverId && (
                        <span className="found-where ellipsis">
                          #{channelName(message.channel_id) ?? "канал"}
                        </span>
                      )}
                    </header>
                    <div className="found-text">{highlight(plainText(message.content), words)}</div>
                    <footer className="found-foot">
                      <span className="found-at">{dayTime(message.created)}</span>
                      <button
                        type="button"
                        className="found-go"
                        onClick={() => onOpen(message)}
                      >
                        Перейти
                      </button>
                    </footer>
                  </article>
                );
              })}
            </>
          )
        )}
      </div>
    </>
  );
}

/**
 * Подсветить слова, совпавшие с набранным по основе: окончание отброшено, так
 * что «кошка» подсвечивает и «кошки». Грубее морфологии сервера, но слова,
 * которые он нашёл, видны.
 */
function highlight(text: string, words: string[]): ReactNode[] {
  const stems = words.map((word) => word.slice(0, Math.max(3, word.length - 2)));
  const parts = text.split(/([\p{L}\p{N}]+)/u);
  return parts.map((part, index) =>
    stems.some((stem) => part.toLowerCase().startsWith(stem)) ? <mark key={index}>{part}</mark> : part,
  );
}
